<template>
  <Sheet>
    <template #leading>
      <Sheet.Button :icon="CloseIcon" @click="handleClose" />
    </template>
    <template #title>Confirm your payment</template>

    <Sheet.Body class="pt-[15px]">
      <div
        class="flex items-center justify-between border-b border-gray-800/10 pb-[15px]"
      >
        <div class="text-[14px] leading-[22px] text-gray-800">
          {{ formatNumber(product.credits) }} report{{
            product.credits > 1 ? 's' : ''
          }}
        </div>
        <button type="button" class="text-link" @click="handleChange">
          Change
        </button>
      </div>

      <div
        class="mt-[15px] flex items-center justify-between border-b border-gray-800/10 pb-[15px]"
      >
        <div class="text-[14px] leading-[22px] text-gray-500">Total</div>
        <div class="text-[14px] leading-[22px] font-medium text-gray-800">
          {{ total }}
        </div>
      </div>

      <div class="mt-[15px] flex items-start justify-between">
        <div class="text-[14px] leading-[22px] text-gray-500">
          Payment method
        </div>
        <div class="text-right">
          <div
            v-if="paymentMethod?.card"
            class="flex items-center justify-end gap-x-2.5"
          >
            <CreditCard :type="paymentMethod.card.type" />
            <div class="text-[14px] leading-[22px] font-medium text-gray-800">
              •••• •••• •••• {{ paymentMethod.card.last4 }}
            </div>
          </div>
          <button type="button" class="text-link" @click="handleChangePayment">
            {{ paymentMethod ? 'Change' : 'Add' }}
          </button>
        </div>
      </div>

      <Alert v-if="error" variant="error" :icon="AlertIcon" class="mt-4">
        {{ error }}
      </Alert>
    </Sheet.Body>

    <div class="shrink-0 px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+16px)]">
      <p class="mb-4 text-[12px] leading-[18px] text-gray-600">
        By selecting Agree and pay, you authorize Dacr to charge the amount
        shown above to your card on file. All sales are final except where
        required by law. I agree to the
        <a class="text-link" href="/legal/terms" target="_blank">Dacr Terms</a>,
        <a class="text-link" href="/legal/privacy" target="_blank"
          >Privacy Policy</a
        >, and
        <a class="text-link" href="/legal/payments" target="_blank"
          >Payments Policy</a
        >.
      </p>

      <Button
        variant="primary"
        size="lg"
        class="w-full"
        :disabled="!paymentMethod"
        :loading="loading"
        @click="handlePay"
      >
        Agree and pay
      </Button>
    </div>
  </Sheet>
</template>

<script setup lang="ts">
import { Sheet, Button, Alert } from '@dacr/library'
import { CloseIcon, AlertIcon } from '@dacr/icons'

import CreditCard from '@/features/billing/components/CreditCard.vue'
import {
  usePayReportCredits,
  type PayReportProduct,
} from '../usePayReportCredits'
import { formatNumber } from '@/shared/utils/number'

const props = defineProps<{
  product: PayReportProduct
  afterPurchase?: () => void
}>()

const {
  loading,
  error,
  paymentMethod,
  total,
  handleChangePayment,
  handlePay,
  handleChange,
  handleClose,
} = usePayReportCredits(props.product, props.afterPurchase)
</script>
